import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import Autocomplete from '@mui/material/Autocomplete';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useUserRolePermissions } from 'hooks';
import CounterChip from '../components/CounterChip';
import ProductDetails from './ProductDetails';
import { renderInput } from '../../utils/renderUtils';
import { useGetProductsQuery } from '../../customerAuthorizationsEndpoints';
import { CreateCustomerAuthContext, initialStateStep3, initialStateStep4 } from '../Create';

const resetState = R.mergeLeft(initialStateStep4, initialStateStep3);

export const renderTags = (tags, getTagProps) =>
  tags.map((item, index) => (
    <Chip
      label={item.productNameShort}
      sx={{ borderColor: 'secondary.main', borderRadius: 25 }}
      variant="outlined"
      {...getTagProps({ index })}
    />
  ));

const Products = ({ isSelectedTerminalAuthorized }) => {
  const { info, setInfo } = useContext(CreateCustomerAuthContext);
  const { authorizationType, customerId: customerIdName, products, terminals } = info;

  const { currentAffiliate = {} } = useUserRolePermissions();
  const { customerIdName: companyCustomerIdName, dataCompanyId } = currentAffiliate;

  const params = {
    customerIdName,
    supplier: { companyCustomerIdName, dataCompanyId },
    terminals: terminals.map(R.prop('terminalId')),
  };

  const { data: productsQuery = [], isFetching: isFetchingProducts } = useGetProductsQuery(
    { params },
    {
      skip: !terminals.length,
    }
  );

  const isSpecific = authorizationType === 'SPECIFIC';

  const disabled = R.or(isSelectedTerminalAuthorized, !terminals.length);

  // prettier-ignore
  // handleChange :: _ -> Product -> [Product]
  const handleChange = R.compose(
    setInfo,
    R.curryN(2, R.compose(
      R.mergeLeft(resetState),
      R.assoc('products')
    )),
    R.flatten,
    R.of,
    R.nthArg(1)
  );

  return (
    <Card>
      <CardContent>
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Typography variant="subtitle1">Products</Typography>
          {products.length > 0 && <CounterChip count={products.length} />}
        </Stack>

        <Stack spacing={2}>
          {isSpecific ? (
            <Typography>
              At least 1 product is required for a Product Specific authorization.
            </Typography>
          ) : (
            <Typography>
              You are allowing all products for this authorization. To limit products, return to the
              previous step and select &ldquo;Product Specific&rdquo; as your authorization type.
            </Typography>
          )}

          {isSpecific && (
            <>
              <Autocomplete
                disabled={disabled}
                getOptionLabel={R.compose(
                  R.join(' '),
                  R.props(['productIdName', 'productNameShort'])
                )}
                isOptionEqualToValue={R.eqProps('productId')}
                loading={isFetchingProducts}
                multiple
                onChange={handleChange}
                options={productsQuery}
                renderInput={renderInput({
                  label: 'Search Products',
                  length: products.length,
                  loading: isFetchingProducts,
                })}
                renderTags={renderTags}
                value={info.products}
              />
              <Stack>
                {products.map(product => (
                  <ProductDetails info={info} key={product.productId} product={product} />
                ))}
              </Stack>
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

Products.propTypes = {
  isSelectedTerminalAuthorized: PropTypes.bool.isRequired,
};

export default Products;
